import React, {forwardRef, useMemo} from 'react'
import {Badge} from '@components/common/base/badge'
import {getRing} from '@components/common/base/common/ring'
import {cn} from '@containers/html-utils'
import {AvatarGroupTooltip} from './avatar-group-tooltip'
import {TwAvatarGroup} from './containers'
import {AvatarGroupBaseProps} from './types'
import {getOutlineSize} from './utils'
import {AvatarBase} from '.'

export const AvatarGroupBase = forwardRef(
  (
    {
      users,
      size,
      maxUsers = 5,
      className,
      outlineColor = 'grey-l100',
      outlineSize,
      badgeVariant = 'default',
      hideOutline = false,
      hideStatus = false,
      disabled,
      needTooltip,
      onClick,
      refOnFirst,
      avatarOutlineClassName,
      badgeOutlineClassName
    }: AvatarGroupBaseProps,
    ref
  ) => {
    const displayedUsers = useMemo(
      () => users.slice(0, maxUsers),
      [users, maxUsers]
    )
    const restCount = users.length - displayedUsers.length

    if (!users.length) {
      return null
    }

    const content = (
      <TwAvatarGroup
        ref={!refOnFirst ? ref : undefined}
        className={className}
        $disabled={disabled}
        $interactable={!!onClick}
        onClick={onClick}
      >
        {displayedUsers.map((user, i) => (
          <AvatarBase
            ref={refOnFirst && !i ? ref : undefined}
            key={user.userId}
            user={user}
            size={size}
            hideStatus={hideStatus}
            hideHint={needTooltip}
            hideOutline={hideOutline}
            outlineColor={outlineColor}
            outlineSize={outlineSize}
            className={cn('flex-none', '-ml-2', 'first:ml-0')}
            outlineClassName={avatarOutlineClassName}
          />
        ))}
        {restCount > 0 && (
          <Badge
            type="rounded"
            variant={badgeVariant}
            size={size}
            className={cn(
              'flex-none',
              '-ml-2',
              'z-10',
              getRing(outlineSize ?? getOutlineSize(size), outlineColor),
              badgeOutlineClassName
            )}
          >{`+${restCount}`}</Badge>
        )}
      </TwAvatarGroup>
    )

    if (needTooltip) {
      return (
        <AvatarGroupTooltip
          content={content}
          users={users}
          arrowPosition="up-left"
        />
      )
    }

    return content
  }
)
